/* Snugloo storefront behaviour. Loaded as a module after Horizon's own scripts.
   Everything here enhances server-rendered markup; the page works without it. */
import { QuantitySelectorUpdateEvent } from '@theme/events';

const locale = document.documentElement.lang || 'en';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- business-day maths (delivery estimates) ---------- */
function addBusinessDays(from, days) {
  const d = new Date(from);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) added += 1;
  }
  return d;
}

const fmtDay = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });

const fmtDate = new Intl.DateTimeFormat(locale, { day: 'numeric' });

/* "Sep 21–23" inside one month, "Sep 30 – Oct 9" across two: short enough for a phone. */
function formatRange(a, b) {
  const left = fmtDay.format(a);
  if (a.toDateString() === b.toDateString()) return left;
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${left}–${fmtDate.format(b)}`;
  }
  return `${left} – ${fmtDay.format(b)}`;
}

function renderDelivery(root) {
  const n = (k) => parseInt(root.dataset[k], 10) || 0;
  const today = new Date();
  const shipA = addBusinessDays(today, n('procMin'));
  const shipB = addBusinessDays(today, n('procMax'));
  const arriveA = addBusinessDays(shipA, n('transitMin'));
  const arriveB = addBusinessDays(shipB, n('transitMax'));

  const values = {
    today: fmtDay.format(today),
    ship: formatRange(shipA, shipB),
    arrive: formatRange(arriveA, arriveB),
  };

  root.querySelectorAll('[data-pl-date]').forEach((el) => {
    const value = values[el.dataset.plDate];
    /* Compare first: an unconditional write would re-trigger the observer below forever. */
    if (value && el.textContent !== value) el.textContent = value;
  });
}

/* Horizon morphs the product section on variant change, which puts the server's
   fallback text back; watch each timeline and re-apply the dates. */
function watchDelivery(root) {
  renderDelivery(root);
  if (root.dataset.plWatching) return;
  root.dataset.plWatching = '1';
  new MutationObserver(() => renderDelivery(root)).observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

/* ---------- christmas cut-off countdown (real date from theme settings) ---------- */
function renderCutoff(root) {
  const deadline = new Date(root.dataset.deadline);
  if (Number.isNaN(deadline.getTime())) return;
  const out = root.querySelector('[data-pl-countdown]');

  const tick = () => {
    const ms = deadline.getTime() - Date.now();
    if (ms <= 0) {
      root.hidden = true;
      return false;
    }
    root.hidden = false;
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    if (out) out.textContent = days > 0 ? `${days}d ${hours}h` : `${hours}h ${mins}m`;
    return true;
  };

  if (tick()) setInterval(tick, 60000);
}

/* ---------- bundle picker -> Horizon quantity selector ---------- */
let bundlePresetUsed = false;
function productFormFor(el) {
  const section = el.closest('.shopify-section') || document;
  return section.querySelector('product-form-component');
}

function setFormQuantity(bundle, qty) {
  const form = productFormFor(bundle);
  const selector = form?.querySelector('quantity-selector-component');
  if (selector && typeof selector.setValue === 'function') {
    selector.setValue(String(qty));
    selector.updateButtonStates?.();
    selector.onQuantityChange?.();
  } else {
    const input = form?.querySelector('input[name="quantity"]');
    if (input) input.value = String(qty);
  }
}

/* Packs of 2+: each item row (blocks/pl-bundle.liquid) picks its own variant.
   A later option only offers values that exist with the earlier picks
   (Gray slippers come only in EU 40-44); the row stores the variant it lands on. */
function initUnits(bundle) {
  const data = bundle.querySelector('[data-pl-variants]');
  if (!data) return;
  let variants;
  try {
    variants = JSON.parse(data.textContent);
  } catch (e) {
    return;
  }
  const resolve = (row) => {
    const selects = [...row.querySelectorAll('[data-pl-unit-option]')];
    for (let i = 1; i < selects.length; i++) {
      const before = selects.slice(0, i).map((s) => s.value);
      const ok = new Set(
        variants.filter((v) => v.a && before.every((value, k) => v.o[k] === value)).map((v) => v.o[i])
      );
      [...selects[i].options].forEach((o) => {
        o.hidden = !ok.has(o.value);
        o.disabled = !ok.has(o.value);
      });
      if (!ok.has(selects[i].value)) {
        const first = [...selects[i].options].find((o) => ok.has(o.value));
        if (first) selects[i].value = first.value;
      }
    }
    const picked = selects.map((s) => s.value);
    const match = variants.find((v) => v.o.length === picked.length && v.o.every((value, k) => value === picked[k]));
    row.dataset.variantId = match && match.a ? String(match.id) : '';
    row.classList.toggle('is-unavailable', !(match && match.a));
    const img = row.querySelector('[data-pl-unit-img]');
    if (img && match && match.img) img.src = match.img;
  };
  bundle.querySelectorAll('[data-pl-unit]').forEach((row) => {
    resolve(row);
    row.addEventListener('change', () => resolve(row));
  });
}

function initBundle(bundle) {
  const radios = [...bundle.querySelectorAll('input[type="radio"]')];
  if (!radios.length) return;
  initUnits(bundle);

  const summary = bundle.querySelector('[data-pl-bundle-summary]');
  const apply = () => {
    const picked = radios.find((r) => r.checked);
    if (!picked) return;
    setFormQuantity(bundle, parseInt(picked.value, 10) || 1);
    if (summary && picked.dataset.summary) summary.innerHTML = picked.dataset.summary;
  };

  radios.forEach((r) => r.addEventListener('change', apply));

  /* ?bundle=2 preselects a pack once per page load, not again after a size change */
  if (!bundlePresetUsed) {
    bundlePresetUsed = true;
    const wanted = new URLSearchParams(location.search).get('bundle');
    const preset = radios.find((r) => r.value === wanted);
    if (preset) preset.checked = true;
  }

  /* Quantity selector exists only after Horizon upgrades it. */
  customElements.whenDefined('quantity-selector-component').then(apply);

  /* Keep the cards honest if someone edits quantity elsewhere (sticky bar, +/-). */
  const section = bundle.closest('.shopify-section');
  section?.addEventListener('quantity-selector:update', (event) => {
    if (bundle.contains(event.target)) return;
    const qty = Number(event.detail?.quantity);
    if (!qty || event.detail?.cartLine) return;
    const match = radios.find((r) => Number(r.value) === qty);
    radios.forEach((r) => (r.checked = r === match));
    if (summary) summary.innerHTML = match && match.dataset.summary ? match.dataset.summary : '';
  });
}

/* Links like <a data-pl-pick="2" href="#pl-buy"> pick a bundle and scroll to the buy box. */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-pl-pick]');
  if (!link) return;
  const radio = document.querySelector(`.pl-bundle input[value="${link.dataset.plPick}"]`);
  if (!radio) return;
  event.preventDefault();
  radio.checked = true;
  radio.dispatchEvent(new Event('change', { bubbles: true }));
  const target = radio.closest('.pl-bundle');
  target?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
  radio.focus({ preventScroll: true });
});

/* ---------- cart: "add another set" uses Horizon's own line update path ---------- */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-pl-bump]');
  if (!button) return;
  event.preventDefault();
  const line = parseInt(button.dataset.line, 10);
  const qty = parseInt(button.dataset.qty, 10);
  if (!line || !qty) return;
  button.setAttribute('aria-busy', 'true');
  button.dispatchEvent(new QuantitySelectorUpdateEvent(qty, line));
});

/* ---------- home: sticky mobile CTA once the hero leaves the screen ---------- */
function initSticky(bar) {
  const watch = document.querySelector(bar.dataset.watch || '.pl-hero');
  const footer = document.querySelector('.pl-footer');
  if (!watch || !('IntersectionObserver' in window)) return;
  let heroGone = false;
  let footerIn = false;
  const sync = () => bar.classList.toggle('is-visible', heroGone && !footerIn);
  new IntersectionObserver(([e]) => { heroGone = !e.isIntersecting; sync(); }).observe(watch);
  if (footer) new IntersectionObserver(([e]) => { footerIn = e.isIntersecting; sync(); }).observe(footer);
}

/* ---------- decorative motion only while it is on screen ---------- */
const liveObserver = 'IntersectionObserver' in window && !reducedMotion
  ? new IntersectionObserver((entries) => {
      entries.forEach((e) => e.target.classList.toggle('is-live', e.isIntersecting));
    }, { rootMargin: '80px 0px' })
  : null;

/* ---------- before / after slider ---------- */
function initSlider(root) {
  const range = root.querySelector('.pl-compare-slider__range');
  if (!range) return;
  const set = () => root.style.setProperty('--pl-pos', `${range.value}%`);
  range.addEventListener('input', set);
  set();
}

/* ---------- back to top (appears after the first screenful or two) ----------
   Horizon scrolls .page-wrapper instead of the window from 990px up, so the
   button follows whichever element is actually scrolling. */
function pageScroller() {
  const wrap = document.querySelector('.page-wrapper');
  if (wrap && /(auto|scroll)/.test(getComputedStyle(wrap).overflowY) && wrap.scrollHeight > wrap.clientHeight) return wrap;
  return document.scrollingElement || document.documentElement;
}

function initBackToTop() {
  if (document.querySelector('.pl-top')) return;
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'pl-top';
  button.tabIndex = -1;
  button.setAttribute('aria-label', 'Back to top');
  button.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 14l6-6 6 6"/></svg>';
  button.addEventListener('click', () => {
    pageScroller().scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
  });
  document.body.appendChild(button);
  let ticking = false;
  const sync = () => {
    ticking = false;
    const shown = pageScroller().scrollTop > window.innerHeight * 1.5;
    button.classList.toggle('is-shown', shown);
    button.tabIndex = shown ? 0 : -1;
  };
  /* scroll does not bubble, but it can be captured: this sees the window and .page-wrapper alike */
  document.addEventListener('scroll', () => {
    if (!ticking) { ticking = true; requestAnimationFrame(sync); }
  }, { passive: true, capture: true });
  window.addEventListener('resize', sync, { passive: true });
  sync();
}

/* ---------- sticky add-to-cart alongside Shopify Inbox ----------
   Horizon never shows its sticky bar while the chat widget exists. The chat
   launcher sits above the bar's height, so show the bar anyway with Horizon's
   own rule: buy buttons scrolled away, footer not yet in view. */
function initStickyWithChat() {
  const bar = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__bar');
  const buy = document.querySelector('.buy-buttons-block');
  const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
  if (!bar || !buy || !('IntersectionObserver' in window) || bar.dataset.plChat) return;
  bar.dataset.plChat = '1';
  let buyGone = false;
  let footerIn = false;
  const sync = () => {
    if (!document.querySelector('shopify-chat')) return; /* no chat: Horizon handles it */
    const stuck = String(buyGone && !footerIn);
    if (bar.dataset.stuck !== stuck) bar.dataset.stuck = stuck;
  };
  new IntersectionObserver(([e]) => {
    const r = e.boundingClientRect;
    buyGone = !e.isIntersecting && (r.bottom < 0 || r.top < 0);
    sync();
  }).observe(buy);
  if (footer) {
    new IntersectionObserver(([e]) => { footerIn = e.isIntersecting; sync(); }, { rootMargin: '200px 0px 0px 0px' }).observe(footer);
  }
}

/* ---------- size change: refresh the blocks Horizon does not update ----------
   Horizon's variant picker fetches the section for the new variant and lets
   its own components update themselves. The price and the pack cards are
   ours, so swap them in from that same response, keeping the chosen pack. */
document.addEventListener('shopify:product:select', (event) => {
  const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
  if (!section || !event.promise || !section.querySelector('.pl-bundle, .pl-price')) return;
  event.promise.then((result) => {
    const html = result && result.detail && result.detail.html;
    if (!html || result.detail.newProduct) return;
    ['.pl-price', '.pl-bundle'].forEach((selector) => {
      const current = section.querySelector(selector);
      const fresh = html.querySelector(selector);
      if (!current || !fresh) return;
      const next = document.importNode(fresh, true);
      if (selector === '.pl-bundle') {
        const picked = current.querySelector('input[type="radio"]:checked');
        if (picked) {
          next.querySelectorAll('input[type="radio"]').forEach((r) => {
            r.checked = r.value === picked.value;
          });
        }
        /* Item 1 follows the new main pick; items 2+ keep what the shopper chose. */
        current.querySelectorAll('[data-pl-units]').forEach((units) => {
          const fresh = next.querySelector(`[data-pl-units="${units.dataset.plUnits}"]`);
          if (!fresh) return;
          const oldRows = [...units.querySelectorAll('[data-pl-unit]')];
          const newRows = [...fresh.querySelectorAll('[data-pl-unit]')];
          oldRows.forEach((row, i) => {
            if (i === 0 || !newRows[i]) return;
            const values = [...row.querySelectorAll('[data-pl-unit-option]')].map((s) => s.value);
            newRows[i].querySelectorAll('[data-pl-unit-option]').forEach((s, k) => {
              if (values[k] !== undefined) s.value = values[k];
            });
          });
        });
      }
      current.replaceWith(next);
    });
    boot(section);
  }).catch(() => {});
});

/* ---------- boot (also re-runs for editor + section re-renders) ---------- */
function boot(scope = document) {
  scope.querySelectorAll('[data-pl-delivery]').forEach(watchDelivery);
  scope.querySelectorAll('[data-pl-cutoff]').forEach((c) => {
    if (c.dataset.plReady) return;
    c.dataset.plReady = '1';
    renderCutoff(c);
  });
  scope.querySelectorAll('.pl-bundle').forEach((b) => {
    if (b.dataset.plReady) return;
    b.dataset.plReady = '1';
    initBundle(b);
  });
  if (liveObserver) {
    scope.querySelectorAll('.pl-lights, .pl-snowfall, .pl-announce, .pl-ticker').forEach((el) => {
      if (el.dataset.plLive) return;
      el.dataset.plLive = '1';
      liveObserver.observe(el);
    });
  }
  scope.querySelectorAll('.pl-sticky').forEach((s) => {
    if (s.dataset.plReady) return;
    s.dataset.plReady = '1';
    initSticky(s);
  });
  scope.querySelectorAll('[data-pl-slider]').forEach((el) => {
    if (el.dataset.plReady) return;
    el.dataset.plReady = '1';
    initSlider(el);
  });
}

boot();
initBackToTop();
initStickyWithChat();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
